import { Alert, Button, Flex, Spin, message } from "antd"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadDocument } from "../api/signing.services"
import PdfViewer from "./PdfViewer"

interface Props {
  session: string
  documentUrl: string | null
  documentName: string
  isSigner: boolean
}

/**
 * The document itself, shown from the blob the page already holds.
 *
 * Saving it asks the server again rather than reusing that blob: a download is
 * its own event, and one taken off a URL the browser already has is one the
 * audit trail never hears about.
 */
const DocumentStep: React.FC<Props> = ({
  session,
  documentUrl,
  documentName,
  isSigner
}) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  const save = useCallback(async () => {
    setSaving(true)
    const response = await downloadDocument(session, documentName)
    setSaving(false)
    if (!response.ok) void message.error(t("signing.document.failed"))
  }, [session, documentName, t])

  if (!documentUrl) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <PdfViewer url={documentUrl} height="60vh" />

      <Flex gap={8} wrap>
        <Button
          size="large"
          loading={saving}
          onClick={() => void save()}
          style={{ flex: "1 1 200px", minHeight: 44 }}
        >
          {t("signing.document.download")}
        </Button>
      </Flex>

      {isSigner && (
        <Alert
          type="info"
          showIcon
          message={t("signing.document.signingComesLater")}
        />
      )}
    </Flex>
  )
}

export default DocumentStep
